/**
 * Sticky formatting toolbar for the artifact editor.
 *
 * Renders against a TipTap `Editor` instance and dispatches commands via
 * `editor.chain()`. Keep the toolbar visually quiet — it sits between the
 * canvas header and the document body, so loud styling distracts from the
 * content the user is actually editing.
 *
 * @module components/artifacts/editor-toolbar
 */

import type { Editor } from "@tiptap/core";
import {
  Bold,
  Code,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Strikethrough,
  Undo2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface EditorToolbarProps {
  /**
   * Active editor instance. Toolbar is hidden while null (during initial
   * mount or when the editor is destroyed) — there is nothing to act on.
   */
  editor: Editor | null;
  /**
   * Disables every button when true. Used during artifact streaming so the
   * user cannot mutate the document while the model is still writing it.
   */
  disabled?: boolean;
}

/**
 * Block style options surfaced in the heading select. Maps user-facing
 * labels to TipTap commands; mirrors what `tiptap-markdown` round-trips
 * cleanly so the on-disk markdown stays predictable.
 */
const BLOCK_STYLES = [
  { value: "paragraph", label: "Paragraph" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
] as const;

/**
 * Renders the formatting toolbar. The select picks the current block's
 * style; the button row toggles inline marks and lists/quotes/links.
 */
export function EditorToolbar({ editor, disabled = false }: EditorToolbarProps) {
  if (!editor) return null;

  const currentBlock = readCurrentBlock(editor);

  /**
   * Apply a new block style to the current selection. Headings 1-3 use the
   * `setHeading` command; "paragraph" reverts via `setParagraph`.
   */
  function setBlock(value: string) {
    if (disabled) return;
    if (value === "paragraph") {
      editor!.chain().focus().setParagraph().run();
    } else if (value === "h1" || value === "h2" || value === "h3") {
      const level = Number(value.slice(1)) as 1 | 2 | 3;
      editor!.chain().focus().setHeading({ level }).run();
    }
  }

  /**
   * Prompts for a URL and toggles the link mark on the current selection.
   * Clearing the input removes the link entirely so users can undo a link
   * without selecting "remove link" from a separate menu.
   */
  function toggleLink() {
    if (disabled) return;
    const previous = editor!.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", previous ?? "");
    if (url === null) return;
    if (url === "") {
      editor!.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor!.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Select value={currentBlock} onValueChange={setBlock} disabled={disabled}>
        <SelectTrigger className="h-8 w-[120px] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {BLOCK_STYLES.map((style) => (
            <SelectItem key={style.value} value={style.value}>
              {style.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ToolbarDivider />

      <ToolbarButton
        icon={Bold}
        label="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
      />
      <ToolbarButton
        icon={Italic}
        label="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
      />
      <ToolbarButton
        icon={Strikethrough}
        label="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={disabled}
      />
      <ToolbarButton
        icon={Code}
        label="Code"
        active={editor.isActive("code")}
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={disabled}
      />

      <ToolbarDivider />

      <ToolbarButton
        icon={List}
        label="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
      />
      <ToolbarButton
        icon={ListOrdered}
        label="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
      />
      <ToolbarButton
        icon={Quote}
        label="Quote"
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        disabled={disabled}
      />
      <ToolbarButton
        icon={LinkIcon}
        label="Link"
        active={editor.isActive("link")}
        onClick={toggleLink}
        disabled={disabled}
      />

      <ToolbarDivider />

      <ToolbarButton
        icon={Undo2}
        label="Undo"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
      />
      <ToolbarButton
        icon={Redo2}
        label="Redo"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
      />
    </div>
  );
}

interface ToolbarButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * Single toggle button. Pressed state is communicated via background tint
 * and an `aria-pressed` attribute so screen readers announce the change.
 */
function ToolbarButton({
  icon: Icon,
  label,
  active = false,
  disabled = false,
  onClick,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors",
        "hover:bg-muted hover:text-foreground",
        "disabled:pointer-events-none disabled:opacity-40",
        active && "bg-muted text-foreground",
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}

/** Vertical separator between toolbar groups. */
function ToolbarDivider() {
  return <div className="mx-1 h-5 w-px bg-border" />;
}

/**
 * Reads the active block style for the heading select. Only checks the
 * three styles the select offers; anything else (code blocks, etc.) falls
 * through to "paragraph" so the select still has a sensible value.
 */
function readCurrentBlock(editor: Editor): string {
  if (editor.isActive("heading", { level: 1 })) return "h1";
  if (editor.isActive("heading", { level: 2 })) return "h2";
  if (editor.isActive("heading", { level: 3 })) return "h3";
  return "paragraph";
}
